import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import NavLink from './common/NavLink';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box } from '@mui/material';
import RequireVip from './RequireVip'
import { Helmet } from 'react-helmet-async'; 
import { ThemeProvider } from '@mui/material/styles';
import { darkTheme } from './theme';
import { slugify } from '../utils/slugify'; 
import { useCompetitions } from './CompetitionContext';

// Słownik mapujący kraje na federacje kontynentalne
const CONTINENT_MAPPING = {
    // UEFA (Europa)
    "Hiszpania": "Europa (UEFA)", "Francja": "Europa (UEFA)", "Anglia": "Europa (UEFA)", 
    "Portugalia": "Europa (UEFA)", "Niemcy": "Europa (UEFA)", "Holandia": "Europa (UEFA)", 
    "Chorwacja": "Europa (UEFA)", "Norwegia": "Europa (UEFA)", "Szwajcaria": "Europa (UEFA)", 
    "Turcja": "Europa (UEFA)", "Belgia": "Europa (UEFA)", "Austria": "Europa (UEFA)", 
    "Szkocja": "Europa (UEFA)", "Czechy": "Europa (UEFA)", "Szwecja": "Europa (UEFA)", 
    "Bośnia i Hercegowina": "Europa (UEFA)",

    // CONMEBOL (Ameryka Południowa)
    "Argentyna": "Ameryka Południowa (CONMEBOL)", "Brazylia": "Ameryka Południowa (CONMEBOL)", 
    "Kolumbia": "Ameryka Południowa (CONMEBOL)", "Ekwador": "Ameryka Południowa (CONMEBOL)", 
    "Urugwaj": "Ameryka Południowa (CONMEBOL)", "Paragwaj": "Ameryka Południowa (CONMEBOL)",

    // CONCACAF (Ameryka Północna i Środkowa)
    "Meksyk": "Ameryka Płn. i Środkowa (CONCACAF)", "USA": "Ameryka Płn. i Środkowa (CONCACAF)", 
    "Kanada": "Ameryka Płn. i Środkowa (CONCACAF)", "Panama": "Ameryka Płn. i Środkowa (CONCACAF)", 
    "Haiti": "Ameryka Płn. i Środkowa (CONCACAF)", "Curaçao": "Ameryka Płn. i Środkowa (CONCACAF)",

    // CAF (Afryka)
    "Senegal": "Afryka (CAF)", "Maroko": "Afryka (CAF)", "Algieria": "Afryka (CAF)", 
    "Egipt": "Afryka (CAF)", "Wybrzeże Kości Słoniowej": "Afryka (CAF)", 
    "Demokratyczna Republika Konga": "Afryka (CAF)", "Tunezja": "Afryka (CAF)", 
    "Republika Zielonego Przylądka": "Afryka (CAF)", "Republika Południowej Afryki": "Afryka (CAF)", 
    "Ghana": "Afryka (CAF)",

    // AFC (Azja - wliczając Australię)
    "Japonia": "Azja (AFC)", "Australia": "Azja (AFC)", "Korea Południowa": "Azja (AFC)", 
    "Iran": "Azja (AFC)", "Uzbekistan": "Azja (AFC)", "Jordania": "Azja (AFC)", 
    "Irak": "Azja (AFC)", "Arabia Saudyjska": "Azja (AFC)", "Katar": "Azja (AFC)",

    // OFC (Oceania)
    "Nowa Zelandia": "Oceania (OFC)"
};

const CompetitionEloRanking = () => {
    // Zachowano id 431
    const competitionId = 431;
    const { competitions } = useCompetitions();
    const [data, setData] = useState({ competition: { name: 'Liga', groupName: null }, teams: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [sortConfig, setSortConfig] = useState({ key: 'eloRanking', direction: 'desc' });
    const [selectedComp, setSelectedComp] = useState(null);

    // Pobieranie danych z API
    useEffect(() => {
        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_URL}/api/competition-elo/431`)
            .then(response => {
                const sortedTeams = [...response.data.teams].sort((a, b) => b.eloRanking - a.eloRanking);
                setData({ competition: response.data.competition, teams: sortedTeams });
                setLoading(false);
            })
            .catch(error => {
                console.error('Błąd podczas pobierania rankingu Elo:', error);
                setLoading(false);
            });
    }, [competitionId]);
    
    useEffect(() => {
        if (loading) return;
        const compId = 431;
        if (!compId) {
            setError('Liga nie znaleziona.');
            return;
        }
        setSelectedComp(compId);
    }, [competitionId, competitions, loading]);

    // Funkcja wyliczająca statystyki kontynentalne
    const continentData = useMemo(() => {
        if (!data.teams || data.teams.length === 0) return [];
        
        const aggregates = {};
        data.teams.forEach(team => {
            const continent = CONTINENT_MAPPING[team.name] || "Inne";
            aggregates[continent] = (aggregates[continent] || 0) + team.eloChangeSum;
        });

        return Object.entries(aggregates)
            .map(([name, eloChangeSum]) => ({ name, eloChangeSum }))
            .sort((a, b) => b.eloChangeSum - a.eloChangeSum); // Sortowanie malejące
    }, [data.teams]);

    // Zmodyfikowana funkcja generująca gradient (przyjmuje min i max by móc działać dla obu tabel)
    const getEloChangeGradientColor = (value, minVal, maxVal) => {
        if (value === 0) return 'rgba(255, 255, 255, 0.2)';

        const maxAbs = Math.max(Math.abs(minVal), Math.abs(maxVal));
        const scaledValue = maxAbs === 0 ? 0 : Math.abs(value) / maxAbs; 

        const grayBase = 255;
        let r, g, b, alpha;

        if (value > 0) {
            r = Math.round(grayBase * (1 - scaledValue));
            g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
            b = Math.round(grayBase * (1 - scaledValue));
        } else {
            r = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
            g = Math.round(grayBase * (1 - scaledValue));
            b = Math.round(grayBase * (1 - scaledValue));
        }

        alpha = 0.3 + scaledValue * 0.7; 
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Funkcja sortowania dla głównej tabeli
    const sortData = (key) => {
        let direction = 'desc'; 
        if (key === 'name' && sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc'; 
        }
        setSortConfig({ key, direction });

        const sortedTeams = [...data.teams].sort((a, b) => {
            if (key === 'name') {
                return direction === 'asc' 
                    ? a.name.localeCompare(b.name) 
                    : b.name.localeCompare(a.name);
            }
            return direction === 'asc' 
                ? a[key] - b[key] 
                : b[key] - a[key];
        });
        setData({ ...data, teams: sortedTeams });
    };

    const getHeaderTitle = () => {
        const leagueName = data.competition.name || 'Liga';
        const groupName = data.competition.groupName;
        return groupName && groupName.trim() 
            ? `Ranking Elo - ${leagueName} (${groupName.trim()})`
            : `Ranking Elo - ${leagueName}`;
    };
    
    const pageTitle = `${getHeaderTitle()} | SymuLiga`;
    const pageDesc = `Aktualny ranking Elo dla ${data.competition.name}. Sprawdź siłę drużyn i zmiany rankingu.`;

    if (loading) {
        return (
            <ThemeProvider theme={darkTheme}>
                <Box sx={{ padding: { xs: 2, sm: 4 } }}>
                    <Typography variant="h6">Ładowanie danych...</Typography>
                </Box>
            </ThemeProvider>
        );
    }

    if (data.teams.length === 0) {
        return (
            <ThemeProvider theme={darkTheme}>
                <Box sx={{ padding: { xs: 2, sm: 4 } }}>
                    <Typography variant="h6">Brak danych dla tej ligi.</Typography>
                </Box>
            </ThemeProvider>
        );
    }

    // Min i max dla tabeli drużyn
    const minEloChange = Math.min(...data.teams.map(team => team.eloChangeSum));
    const maxEloChange = Math.max(...data.teams.map(team => team.eloChangeSum));

    // Min i max dla tabeli kontynentów
    const minContinentChange = continentData.length > 0 ? Math.min(...continentData.map(c => c.eloChangeSum)) : 0;
    const maxContinentChange = continentData.length > 0 ? Math.max(...continentData.map(c => c.eloChangeSum)) : 0;

    return (
        <ThemeProvider theme={darkTheme}>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDesc} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDesc} />
            </Helmet>
            <Box sx={{ padding: { xs: 2, sm: 4 } }}>
                <Typography variant="h5" component="h1" sx={{ textAlign: { xs: 'center', sm: 'center' }, padding: '10px 10px', fontSize: { xs: '1.0rem', sm: '1.2rem' } }}>
                    {getHeaderTitle()}
                </Typography>
                
                {/* GŁÓWNA TABELA - ZESPOŁY */}
                <TableContainer component={Paper} sx={{ width: 'fit-content', margin: '0 auto', mb: 6 }}>
                    <Table sx={{ width: 'auto' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', padding: '6px' }}>#</TableCell>
                                <TableCell onClick={() => sortData('name')} sx={{ cursor: 'pointer', fontWeight: 'bold', padding: '6px' }}>
                                    Nazwa drużyny
                                </TableCell>
                                <TableCell onClick={() => sortData('eloRanking')} sx={{ cursor: 'pointer', fontWeight: 'bold', textAlign: 'center', padding: '6px' }}>
                                    Elo drużyny
                                </TableCell>
                                <TableCell onClick={() => sortData('eloChangeSum')} sx={{ cursor: 'pointer', fontWeight: 'bold', textAlign: 'center', padding: '6px' }}>
                                    Zmiana Elo
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.teams.map((team, index) => (
                                <TableRow key={team.teamId}>
                                    <TableCell sx={{ textAlign: 'center', padding: '6px' }}>{index + 1}</TableCell>
                                    <TableCell component={NavLink} to={`/team/${team.teamId}/${slugify(team.name)}`} sx={{ padding: '6px' }}>
                                        {team.name}
                                    </TableCell>
                                    
                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', padding: '6px' }}>{team.eloRanking}</TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        backgroundColor: getEloChangeGradientColor(team.eloChangeSum, minEloChange, maxEloChange),
                                        color: team.eloChangeSum / (minEloChange || -1) > 0.5 ? 'white' : 'black',
                                        fontWeight: 'bold',
                                        padding: '6px', 
                                        textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
                                        transition: 'background-color 0.3s',
                                        '&:hover': {
                                            backgroundColor: getEloChangeGradientColor(team.eloChangeSum + (team.eloChangeSum > 0 ? 10 : -10), minEloChange, maxEloChange)
                                        },
                                    }}>
                                        {team.eloChangeSum > 0 ? `+${team.eloChangeSum}` : team.eloChangeSum}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* NOWA TABELA - KONTYNENTY */}
                <Typography variant="h5" component="h2" sx={{ textAlign: 'center', mb: 2, fontSize: { xs: '1.0rem', sm: '1.2rem' } }}>
                    Zmiana Elo wg Kontynentów
                </Typography>
                
                <TableContainer component={Paper} sx={{ width: 'fit-content', margin: '0 auto' }}>
                    <Table sx={{ width: 'auto' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', padding: '6px' }}>Kontynent (Federacja)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', padding: '6px' }}>Suma zmian Elo</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {continentData.map((continent) => (
                                <TableRow key={continent.name}>
                                    <TableCell sx={{ padding: '6px' }}>{continent.name}</TableCell>
                                    <TableCell sx={{
                                        textAlign: 'center',
                                        backgroundColor: getEloChangeGradientColor(continent.eloChangeSum, minContinentChange, maxContinentChange),
                                        color: continent.eloChangeSum / (minContinentChange || -1) > 0.5 ? 'white' : 'black',
                                        fontWeight: 'bold',
                                        padding: '6px',
                                        textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
                                        transition: 'background-color 0.3s',
                                        '&:hover': {
                                            backgroundColor: getEloChangeGradientColor(continent.eloChangeSum + (continent.eloChangeSum > 0 ? 10 : -10), minContinentChange, maxContinentChange)
                                        },
                                    }}>
                                        {continent.eloChangeSum > 0 ? `+${continent.eloChangeSum}` : continent.eloChangeSum}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>
        </ThemeProvider>
    );
};

export default CompetitionEloRanking;